import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { EmptyState } from "@/components/ui/feedback";
import { Checkbox } from "@/components/ui/form-controls";
import { Field, Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Table, TableWrapper } from "@/components/ui/table";
import {
  ErrorState,
  LoadingTable,
  NotAvailableState,
  PermissionDeniedState,
} from "@/components/ui/system-state";
import { SsoProviderForm } from "@/features/admin/sso/provider-form";
import { SsoRoleMappingDialog } from "@/features/admin/sso/role-mapping";
import { verifiedDomains, type SsoDomainsState } from "@/features/admin/sso/use-sso-domains";
import { formatTemplate, type SsoLocaleCatalog } from "@/i18n/use-sso-locale";
import {
  DENIED_REASON_CODES,
  UNAVAILABLE_REASON_CODES,
  formatTimestamp,
  refusalMessage,
} from "@/features/admin/sso/sso-shared";
import type { SsoProvidersState } from "@/features/admin/sso/use-sso-providers";
import {
  disableSsoProvider,
  enableSsoProvider,
  isAdminApiProvider,
  withStepUpRetry,
  type ApiSsoProvider,
} from "@/lib/api";

/**
 * Admin → Single sign-on → Identity providers (ALL-5464, page-spec actions
 * `admin_reads_identity_providers`, `admin_enables_provider`,
 * `admin_disables_provider_break_glass_unaffected`).
 *
 * Every row is whatever getSsoOverview returned: the status is rendered as a
 * word, the issuer, domains and routes come straight from the response, and the
 * client secret is not in the response at all — only when it was set and its
 * fingerprint, which is all this table can show.
 *
 * Refusals from the enable and disable operations render inside the dialog that
 * raised them, with the server's reasonCode text and its copyable support
 * reference (D636), so the operator can correct the input and retry in place;
 * the success line renders on the card, because by then the dialog is gone.
 */

function statusTone(status: string): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "DRAFT":
      return "warning";
    case "DISABLED":
      return "neutral";
    default:
      return "neutral";
  }
}

function statusLabel(ui: Record<string, string>, status: string): string {
  return ui[`sso.providers.status.${status}`] ?? ui["sso.providers.status.unknown"];
}

function roleModeLabel(ui: Record<string, string>, mode: string): string {
  return ui[`sso.providers.roleMode.${mode}`] ?? ui["sso.providers.roleMode.EXPLICIT_ONLY"];
}

interface Denial {
  message: string;
  supportReference?: string;
  capability?: string;
}

/** A provider can be switched on from DRAFT or back on from DISABLED; an ACTIVE
 * one can be switched off. Nothing here decides *whether* that is allowed —
 * the server refuses with its own reasonCode and this page renders it. */
function canEnable(provider: ApiSsoProvider): boolean {
  return provider.status === "DRAFT" || provider.status === "DISABLED";
}

export function AdminSsoProvidersCard({
  canManage,
  catalog,
  state,
  domainsState,
  pageStateOwned = false,
}: {
  canManage: boolean;
  catalog: SsoLocaleCatalog;
  state: SsoProvidersState;
  domainsState: SsoDomainsState;
  /** True when the domains card already states the page-level denied or
   * unavailable state. This card then renders nothing: one page-level fact is
   * stated once, not once per card (see sso-admin.tsx). */
  pageStateOwned?: boolean;
}) {
  const ui = catalog.ui;
  const { providers, loading, error, refresh } = state;

  if (pageStateOwned) return null;
  const [formOpen, setFormOpen] = useState(false);
  const [rolesProvider, setRolesProvider] = useState<ApiSsoProvider | null>(null);
  const [enableProvider, setEnableProvider] = useState<ApiSsoProvider | null>(null);
  const [disableProvider, setDisableProvider] = useState<ApiSsoProvider | null>(null);
  /** The outcome line for a completed enable/disable. Success only: a refusal
   * stays in its dialog until the operator fixes it. */
  const [outcome, setOutcome] = useState<{ testId: string; message: string } | null>(null);

  if (!canManage) {
    return (
      <PermissionDeniedState
        title={ui["sso.shared.deniedTitle"]}
        description={ui["sso.shared.deniedDescription"]}
      />
    );
  }

  if (error && DENIED_REASON_CODES.includes(error.reasonCode)) {
    return (
      <PermissionDeniedState
        title={ui["sso.shared.deniedTitle"]}
        description={ui["sso.shared.deniedDescription"]}
        details={[{ k: "reasonCode", v: error.reasonCode }]}
        footnote={<SupportDetails reference={error.supportReference} capability={error.capability} />}
      />
    );
  }

  if (error && UNAVAILABLE_REASON_CODES.includes(error.reasonCode)) {
    return (
      <div className="mt-5 flex flex-col items-center gap-2">
        <NotAvailableState
          title={ui["sso.shared.unavailableTitle"]}
          description={ui["sso.shared.unavailableDescription"]}
          reason={error.reasonCode}
        />
        <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
      </div>
    );
  }

  return (
    <Card className="mt-5">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{ui["sso.providers.title"]}</CardTitle>
            <p className="mt-2 max-w-[560px] text-xs leading-relaxed text-muted">
              {ui["sso.providers.description"]}
            </p>
          </div>
          {/* `flex-wrap` (already on the header row above) plus a 36px tap
              target on each action: `size="sm"` is 32px tall, below this lane's
              floor for a control a person taps on a phone. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" className="min-h-[36px]" onClick={refresh} disabled={loading}>
              <RefreshCw aria-hidden className="size-3.5" /> {ui["sso.providers.refresh"]}
            </Button>
            <Button size="sm" className="min-h-[36px]" onClick={() => setFormOpen(true)}>
              {ui["sso.providers.addButton"]}
            </Button>
          </div>
        </div>

        {outcome && (
          <div data-testid={outcome.testId}>
            <InfoBanner variant="success" className="mt-4">{outcome.message}</InfoBanner>
          </div>
        )}

        {loading && (
          <div className="mt-4" data-testid="sso-providers-loading">
            <span className="sr-only">{ui["sso.providers.loading"]}</span>
            <LoadingTable rows={3} cols={6} />
          </div>
        )}

        {!loading && error && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <ErrorState
              title={ui["sso.providers.failedToLoad"]}
              details={error.reasonCode}
              retryLabel={ui["sso.shared.retry"]}
              onRetry={refresh}
            />
            <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          </div>
        )}

        {!loading && !error && providers.length === 0 && (
          <div className="mt-4">
            <EmptyState
              card
              title={ui["sso.providers.emptyTitle"]}
              description={ui["sso.providers.emptyDescription"]}
              action={
                <Button size="sm" className="min-h-[36px]" onClick={() => setFormOpen(true)}>
                  {ui["sso.providers.addButton"]}
                </Button>
              }
            />
          </div>
        )}

        {!loading && !error && providers.length > 0 && (
          <div className="mt-4" data-testid="sso-providers-table">
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    {[
                      "sso.providers.table.name",
                      "sso.providers.table.status",
                      "sso.providers.table.issuer",
                      "sso.providers.table.domains",
                      "sso.providers.table.routes",
                      "sso.providers.table.roles",
                      "sso.providers.table.actions",
                    ].map((key) => (
                      <th
                        key={key}
                        className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted"
                      >
                        {ui[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {providers.map((provider) => {
                    const editable = isAdminApiProvider(provider);
                    return (
                      <tr
                        key={provider.identityProviderId}
                        data-testid={`sso-provider-${provider.identityProviderId}`}
                        className="border-b border-line last:border-b-0"
                      >
                        <td className="px-4 py-[9px] text-[11.5px] text-ink">
                          <div className="font-semibold">{provider.displayName}</div>
                          <div className="mt-0.5 text-[10.5px] text-muted">
                            {provider.clientSecretSetAt
                              ? formatTemplate(ui["sso.providers.secretSetAt"], {
                                date: formatTimestamp(provider.clientSecretSetAt),
                                fingerprint: provider.clientSecretFingerprint ?? ui["sso.providers.secretNotSet"],
                              })
                              : ui["sso.providers.secretNotSet"]}
                          </div>
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap">
                          <Badge variant={statusTone(provider.status)} size="sm">
                            {statusLabel(ui, provider.status)}
                          </Badge>
                          {!editable && (
                            <div className="mt-1 text-[10.5px] font-semibold text-muted">
                              {ui["sso.providers.managedByDeployment"]}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-[9px] font-mono text-[11px] text-ink">
                          {provider.issuerOrEntityId}
                        </td>
                        <td className="px-4 py-[9px] text-[11px] text-ink">
                          {provider.domains.length > 0
                            ? provider.domains.map((domain) => (
                              <div key={domain} className="font-mono">{domain}</div>
                            ))
                            : <span className="text-muted">{ui["sso.providers.noDomains"]}</span>}
                        </td>
                        <td className="px-4 py-[9px] text-[11px] text-ink">
                          {provider.routes.length > 0
                            ? provider.routes.map((route) => (
                              <div key={route.routeId} className="font-mono">{route.domain}</div>
                            ))
                            : <span className="text-muted">{ui["sso.providers.noRoutes"]}</span>}
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap text-[11px] text-ink">
                          {roleModeLabel(ui, provider.roleMappingMode)}
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap">
                          {editable ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setRolesProvider(provider)}
                              >
                                {ui["sso.providers.manageRoles"]}
                              </Button>
                              {canEnable(provider) ? (
                                <Button size="sm" onClick={() => setEnableProvider(provider)}>
                                  {ui["sso.providers.enable"]}
                                </Button>
                              ) : provider.status === "ACTIVE" ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setDisableProvider(provider)}
                                >
                                  {ui["sso.providers.disable"]}
                                </Button>
                              ) : null}
                            </div>
                          ) : (
                            // No control at all, and the reason is stated where
                            // the control would be: a provider the admin API did
                            // not create is not editable from this page.
                            <span className="text-[10.5px] text-muted">{ui["sso.providers.managedHint"]}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </div>
        )}
      </CardBody>

      <SsoProviderForm
        open={formOpen}
        onOpenChange={setFormOpen}
        catalog={catalog}
        domainsState={domainsState}
        onCreated={refresh}
      />

      <SsoRoleMappingDialog
        provider={rolesProvider}
        onOpenChange={(open) => {
          if (!open) setRolesProvider(null);
        }}
        catalog={catalog}
        onSaved={refresh}
      />

      <EnableProviderDialog
        provider={enableProvider}
        catalog={catalog}
        domainsState={domainsState}
        onOpenChange={(open) => {
          if (!open) setEnableProvider(null);
        }}
        onEnabled={() => {
          setOutcome({ testId: "sso-enable-success", message: ui["sso.enable.success"] });
          refresh();
        }}
      />

      <DisableProviderDialog
        provider={disableProvider}
        catalog={catalog}
        onOpenChange={(open) => {
          if (!open) setDisableProvider(null);
        }}
        onDisabled={() => {
          setOutcome({ testId: "sso-disable-success", message: ui["sso.disable.success"] });
          refresh();
        }}
      />
    </Card>
  );
}

/**
 * POST /v1/admin/sso/providers/{id}/enable (operationId `enableProvider`). The
 * domain list is offered from the organization's VERIFIED challenges, because
 * the API refuses anything else (SSO_DOMAIN_NOT_VERIFIED) — offering more would
 * offer a guaranteed refusal.
 */
function EnableProviderDialog({
  provider,
  catalog,
  domainsState,
  onOpenChange,
  onEnabled,
}: {
  provider: ApiSsoProvider | null;
  catalog: SsoLocaleCatalog;
  domainsState: SsoDomainsState;
  onOpenChange: (open: boolean) => void;
  onEnabled: () => void;
}) {
  const ui = catalog.ui;
  const [selected, setSelected] = useState<string[]>([]);
  const [notice, setNotice] = useState<Denial | null>(null);
  const [busy, setBusy] = useState(false);
  const options = verifiedDomains(domainsState);

  async function handleEnable() {
    if (!provider) return;
    if (selected.length === 0) {
      setNotice({ message: ui["sso.enable.domainsRequired"] });
      return;
    }
    setNotice(null);
    setBusy(true);
    const outcome = await withStepUpRetry(() => enableSsoProvider(provider.identityProviderId, selected));
    setBusy(false);

    if (outcome.stepUpFailed) {
      setNotice({
        message: ui["sso.shared.stepUpDescription"],
        supportReference: outcome.stepUpSupportReference,
      });
      return;
    }
    if (!outcome.result.ok) {
      setNotice({
        message: refusalMessage(ui, outcome.result.reasonCode) ?? ui["sso.enable.failed"],
        supportReference: outcome.result.supportReference,
        capability: outcome.result.capability,
      });
      return;
    }
    setSelected([]);
    onEnabled();
    onOpenChange(false);
  }

  return (
    <Modal
      open={provider !== null}
      onOpenChange={(next) => {
        if (!busy) {
          if (!next) setSelected([]);
          setNotice(null);
          onOpenChange(next);
        }
      }}
      title={ui["sso.enable.title"]}
      description={ui["sso.enable.description"]}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {ui["sso.shared.cancel"]}
          </Button>
          <Button loading={busy} onClick={() => void handleEnable()}>
            {busy ? ui["sso.enable.busy"] : ui["sso.enable.submit"]}
          </Button>
        </>
      }
    >
      {notice && (
        <div data-testid="sso-enable-notice">
          <InfoBanner variant="danger">
            {notice.message}
            <SupportDetails reference={notice.supportReference} capability={notice.capability} />
          </InfoBanner>
        </div>
      )}

      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-semibold text-ink">{ui["sso.form.domains"]}</legend>
        <p className="text-xs text-muted">{ui["sso.form.domainsHint"]}</p>
        {options.length === 0 ? (
          <p className="rounded-md border border-line bg-bg px-3 py-2 text-xs text-muted">
            {ui["sso.form.domainsNone"]}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {options.map((domain) => (
              <div key={domain} className="flex items-center gap-2">
                <Checkbox
                  ariaLabel={domain}
                  checked={selected.includes(domain)}
                  onCheckedChange={(checked) => {
                    setSelected((current) => (
                      checked ? [...current, domain] : current.filter((item) => item !== domain)
                    ));
                  }}
                  disabled={busy}
                />
                {/* Same as the provider form's domain picker: a long
                    unbreakable domain must not push the dialog wider. */}
                <span className="min-w-0 break-all font-mono text-[11.5px] text-ink">{domain}</span>
              </div>
            ))}
          </div>
        )}
      </fieldset>
    </Modal>
  );
}

/**
 * POST /v1/admin/sso/providers/{id}/disable (operationId `disableProvider`). The
 * confirm step says out loud that break-glass access is unaffected, so nobody
 * has to guess whether they are about to lock the organization out, and the
 * contract-required reason cannot be sent blank.
 */
function DisableProviderDialog({
  provider,
  catalog,
  onOpenChange,
  onDisabled,
}: {
  provider: ApiSsoProvider | null;
  catalog: SsoLocaleCatalog;
  onOpenChange: (open: boolean) => void;
  onDisabled: () => void;
}) {
  const ui = catalog.ui;
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Denial | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleDisable() {
    if (!provider) return;
    if (!reason.trim()) {
      setError(ui["sso.disable.reasonRequired"]);
      return;
    }
    setError(null);
    setNotice(null);
    setBusy(true);
    const outcome = await withStepUpRetry(() => disableSsoProvider(provider.identityProviderId, reason.trim()));
    setBusy(false);

    if (outcome.stepUpFailed) {
      setNotice({
        message: ui["sso.shared.stepUpDescription"],
        supportReference: outcome.stepUpSupportReference,
      });
      return;
    }
    if (!outcome.result.ok) {
      setNotice({
        message: refusalMessage(ui, outcome.result.reasonCode) ?? ui["sso.disable.failed"],
        supportReference: outcome.result.supportReference,
        capability: outcome.result.capability,
      });
      return;
    }
    setReason("");
    onDisabled();
    onOpenChange(false);
  }

  return (
    <Modal
      open={provider !== null}
      onOpenChange={(next) => {
        if (!busy) {
          if (!next) {
            setReason("");
            setError(null);
          }
          setNotice(null);
          onOpenChange(next);
        }
      }}
      title={ui["sso.disable.title"]}
      description={ui["sso.disable.description"]}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {ui["sso.shared.cancel"]}
          </Button>
          <Button loading={busy} onClick={() => void handleDisable()}>
            {busy ? ui["sso.disable.busy"] : ui["sso.disable.submit"]}
          </Button>
        </>
      }
    >
      {notice && (
        <div data-testid="sso-disable-notice">
          <InfoBanner variant="danger">
            {notice.message}
            <SupportDetails reference={notice.supportReference} capability={notice.capability} />
          </InfoBanner>
        </div>
      )}

      <Field
        label={ui["sso.disable.reason"]}
        htmlFor="sso-disable-reason"
        hint={error ? undefined : ui["sso.disable.reasonPlaceholder"]}
        error={error ?? undefined}
        required
      >
        <Input
          id="sso-disable-reason"
          name="reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          autoComplete="off"
          error={Boolean(error)}
          disabled={busy}
        />
      </Field>
    </Modal>
  );
}
